import React, { useState } from "react";
import { Link } from "react-router-dom";
import DriverReviewsModal from "./DriverReviewsModal";

type VeiculoDTO = {
  veiculoId: number;
  modelo: string;
  placa: string;
  capacidade: number;
  fotoUrl?: string;
  seguroUrl?: string;
  licencaRnaatUrl?: string;
};

type AvaliacaoDTO = {
  id: number;
  nota: number;
  comentario?: string;
  usuarioNome?: string;
  usuarioFotoUrl?: string;
  fotosPorPonto?: { fotoId: number; ponto: string; url: string }[];
  fotos?: number[];
};

type MotoristaDTO = {
  id: number;
  nome: string;
  idiomas?: string[];
  fotoId?: number;
  veiculo?: VeiculoDTO;
  avaliacoes: AvaliacaoDTO[];
};

type Props = {
  motoristas: MotoristaDTO[];
};

const DriverDetails: React.FC<Props> = ({ motoristas }) => {
  const BASE_URL = process.env.REACT_APP_API_URL;
  const [selectedDriver, setSelectedDriver] = useState<MotoristaDTO | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  if (!motoristas || motoristas.length === 0) {
    return (
      <div className="card shadow-none mb-0">
        <div className="card-body">
          <h5 className="fs-18 mb-3">Driver Details</h5>
          <p>No drivers linked to this package.</p>
        </div>
      </div>
    );
  }

  const totalMotoristas = motoristas.length;
  const idiomas = Array.from(new Set(motoristas.flatMap((m) => m.idiomas || [])));
  const maiorCapacidade = motoristas.reduce((max, m) => {
    return m.veiculo?.capacidade && m.veiculo.capacidade > max
      ? m.veiculo.capacidade
      : max;
  }, 0);

  const calcularMedia = (avaliacoes: AvaliacaoDTO[]) => {
    if (!avaliacoes || avaliacoes.length === 0) return 0;
    const soma = avaliacoes.reduce((acc, a) => acc + (a.nota || 0), 0);
    return soma / avaliacoes.length;
  };

  const openModal = (driver: MotoristaDTO) => {
    setSelectedDriver(driver);
    setModalOpen(true);
  };

  const closeModal = () => {
    setSelectedDriver(null);
    setModalOpen(false);
  };

  return (
    <div className="card shadow-none mb-0">
      <div className="card-body">
        <h5 className="fs-18 mb-3">Driver Details</h5>

        {/* Resumo geral */}
        <div className="mb-3 p-3 border br-10 bg-light-500">
          <p><strong>Total Drivers:</strong> {totalMotoristas}</p>
          <p><strong>Languages:</strong> {idiomas.length > 0 ? idiomas.join(", ") : "N/A"}</p>
          <p><strong>Max Vehicle Capacity:</strong> {maiorCapacidade > 0 ? maiorCapacidade : "N/A"}</p>
        </div>

        {/* Listagem detalhada */}
        <div className="d-flex flex-wrap gap-3">
          {motoristas.map((m) => {
            const media = calcularMedia(m.avaliacoes);
            return (
              <div key={m.id} className="text-center">
                <div
                  style={{ cursor: "pointer" }}
                  onClick={() => openModal(m)}
                >
                  <img
                    src={
                      m.fotoId
                        ? `${BASE_URL}/uploads/${m.fotoId}`
                        : m.veiculo?.fotoUrl
                        ? `${BASE_URL}${m.veiculo.fotoUrl}`
                        : "assets/img/users/default-user.jpg"
                    }
                    alt={m.nome}
                    className="avatar avatar-lg rounded-circle mb-2"
                  />
                  <h6 className="fw-medium text-truncate mb-1">{m.nome}</h6>
                </div>
                {/* Avaliação real */}
                <div className="text-warning d-flex align-items-center justify-content-center" style={{ fontSize: "0.9rem" }}>
                  <span className="me-1">{media.toFixed(1)}</span>
                  {Array.from({ length: 5 }, (_, i) => (
                    <i
                      key={i}
                      className={`fa fa-star ${i < Math.round(media) ? "text-warning" : "text-muted"}`}
                      style={{ fontSize: "0.85rem" }}
                    />
                  ))}
                </div>
                <p className="fs-13 text-muted mb-0">{m.avaliacoes.length} review{m.avaliacoes.length !== 1 ? "s" : ""}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Modal */}
      {selectedDriver && (
        <DriverReviewsModal
          motorista={selectedDriver}
          open={modalOpen}
          onClose={closeModal}
          baseUrl={BASE_URL!}
        />
      )}
    </div>
  );
};

export default DriverDetails;